import {Component, ViewEncapsulation} from '@angular/core';
import {CommonConstants, RoutesConstants, ImagePathConstants} from '@coreconstant';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {noWhitespaceValidator} from '../core/services/common-service/common.service';
import {lastValueFrom} from 'rxjs';
import {ResponseHandlerService} from '../core/services/common-service/response-handler.service';
import {RouterModule} from '@angular/router';
import {SeoService} from '../core/services/common-service/seo.service';
import {RecaptchaModule} from 'ng-recaptcha-2';
import {FaqCtaComponent} from '../core/component/common/faq-cta/faq-cta.component';
import {WeddingPlanCtaComponent} from '../core/component/common/wedding-plan-cta/wedding-plan-cta.component';
import {MetaDataConstants} from '../core/constants/meta-data-constants';
import {RealWeddingService} from '../core/services/api-service/real-wedding.service';
import {RecaptchaService} from '../core/services/api-service/recaptcha.service';

@Component({
    selector: 'app-submit-wedding',
    imports: [RouterModule, ReactiveFormsModule, RecaptchaModule, FaqCtaComponent, WeddingPlanCtaComponent],
    templateUrl: './submit-wedding.component.html',
    styleUrls: ['../how-it-works/contact-us/contact-us.component.scss', './submit-wedding.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class SubmitWeddingComponent {
    routesConstants = RoutesConstants
    imagePathConstants = ImagePathConstants
    commonConstants = CommonConstants
    submitWeddingForm: FormGroup;
    submitted: boolean = false;
    isSubmitAndShowSuccess: boolean = false;

    constructor(
        private _formBuilder: FormBuilder,
        private _realWeddingService: RealWeddingService,
        private _seoService: SeoService,
        private _recaptchaService: RecaptchaService,
        private _responseHandlerService: ResponseHandlerService,
    ) {
        this.setMetaTags()
    }

    /**
     * Sets the meta tags for the current page by creating a meta tag object and passes it to the SEO service for application.
     * @return {void} Does not return a value.
     */
    setMetaTags() {
        let metaTagObject = {
            title: MetaDataConstants.SUBMIT_REAL_WEDDING.TITLE,
            description: MetaDataConstants.SUBMIT_REAL_WEDDING.DESCRIPTION,
            ogImageUrl: MetaDataConstants.SUBMIT_REAL_WEDDING.OGIMAGEURL,
            ogWebUrl: this.routesConstants.SUBMIT_WEDDING,
        }
        this._seoService.setMetaTags(metaTagObject)
    }

    /**
     * Initializes the component and its dependent processes.
     */
    ngOnInit() {
        this.initializeForm();
    }

    /**
     * Initializes the form and applies necessary validation rules to each control.
     */
    initializeForm() {
        this.submitWeddingForm = this._formBuilder.group({
            name: [null, [Validators.required, noWhitespaceValidator]],
            partnerName: [null, [Validators.required, noWhitespaceValidator]],
            emailAddress: [null, [Validators.required, Validators.pattern(this.commonConstants.EMAIL_REGEX)]],
            venueName: [null, [Validators.required]],
            photographerName: ['', [Validators.required, noWhitespaceValidator]],
            message: [''],
            isPublishedAnywhere: [null, [Validators.required]],
            isPermittedByPhotographer: [null, [Validators.required]],
            photographLink: ['', [Validators.required, Validators.pattern(this.commonConstants.WEBSITE_REGEX)]],
            linkPassword: [''],
            recaptcha: ['', Validators.required],
        });
    }

    /**
     * Handles the resolution of a CAPTCHA, verifying the user's response and updating the form based on the verification result.
     * @param {string} captchaResponse - The CAPTCHA response token generated after user completes the CAPTCHA challenge.
     */
    onCaptchaResolved(captchaResponse: string) {
        let input = {
            token: captchaResponse,
        }
        let verifyCaptchaResponse = this._recaptchaService.verifyCaptcha(input)
        lastValueFrom(verifyCaptchaResponse).then((response: any) => {
            if (response["status"] == true) {
                if(response["data"]["success"] == true){
                    this.submitWeddingForm.patchValue({recaptcha: response["data"]});
                }else{
                    this.submitWeddingForm.patchValue({recaptcha: null});
                }

            }
        }, (error: any) => {
            this._responseHandlerService.handleAPIErrorResponse(error);
        });
    }

    /**
     * Submits the form after validating form, prepares the necessary form data, and initiates the API call.
     */
    submit() {
        this.submitted = true;
        if (this.submitWeddingForm.invalid) {
            this.submitWeddingForm.markAllAsTouched();
            this.submitWeddingForm.markAsDirty();
            return
        }

        const flattenedValues = {
            ...this.submitWeddingForm.value,
        }
        let addUpdateRecord = this._realWeddingService.submitRealWedding(flattenedValues)
        lastValueFrom(addUpdateRecord).then((response: any) => {
            if (response["status"] == true) {
                this.isSubmitAndShowSuccess = true
            }
        }, (error: any) => {
            this._responseHandlerService.handleAPIErrorResponse(error);
        });
    }
}
